import _defineProperty from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/opt/buildhome/repo/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["transition", "transitionEnd"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { startAnimation } from '../../animation/utils/transitions.mjs';
import { setTarget } from './setters.mjs';
import { resolveVariant } from './resolve-dynamic-variants.mjs';
import { transformProps } from '../html/utils/transform.mjs';
import { isWillChangeMotionValue } from '../../value/use-will-change/is.mjs';
function animateVisualElement(visualElement, definition) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  visualElement.notify("AnimationStart", definition);
  var animation;
  if (Array.isArray(definition)) {
    var animations = definition.map(function (variant) {
      return animateVariant(visualElement, variant, options);
    });
    animation = Promise.all(animations);
  } else if (typeof definition === "string") {
    animation = animateVariant(visualElement, definition, options);
  } else {
    var resolvedDefinition = typeof definition === "function" ? resolveVariant(visualElement, definition, options.custom) : definition;
    animation = animateTarget(visualElement, resolvedDefinition, options);
  }
  return animation.then(function () {
    return visualElement.notify("AnimationComplete", definition);
  });
}
function animateVariant(visualElement, variant) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _a;
  var resolved = resolveVariant(visualElement, variant, options.custom);
  var _ref = resolved || {},
    _ref$transition = _ref.transition,
    transition = _ref$transition === void 0 ? visualElement.getDefaultTransition() || {} : _ref$transition;
  if (options.transitionOverride) {
    transition = options.transitionOverride;
  }
  /**
   * If we have a variant, create a callback that runs it as an animation.
   * Otherwise, we resolve a Promise immediately for a composable no-op.
   */
  var getAnimation = resolved ? function () {
    return animateTarget(visualElement, resolved, options);
  } : function () {
    return Promise.resolve();
  };
  /**
   * If we have children, create a callback that runs all their animations.
   * Otherwise, we resolve a Promise immediately for a composable no-op.
   */
  var getChildAnimations = ((_a = visualElement.variantChildren) === null || _a === void 0 ? void 0 : _a.size) ? function () {
    var forwardDelay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    var _transition = transition,
      _transition$delayChil = _transition.delayChildren,
      delayChildren = _transition$delayChil === void 0 ? 0 : _transition$delayChil,
      staggerChildren = _transition.staggerChildren,
      staggerDirection = _transition.staggerDirection;
    return animateChildren(visualElement, variant, delayChildren + forwardDelay, staggerChildren, staggerDirection, options);
  } : function () {
    return Promise.resolve();
  };
  /**
   * If the transition explicitly defines a "when" option, we need to resolve either
   * this animation or all children animations before playing the other.
   */
  var _transition2 = transition,
    when = _transition2.when;
  if (when) {
    var _ref2 = when === "beforeChildren" ? [getAnimation, getChildAnimations] : [getChildAnimations, getAnimation],
      _ref3 = _slicedToArray(_ref2, 2),
      first = _ref3[0],
      last = _ref3[1];
    return first().then(last);
  } else {
    return Promise.all([getAnimation(), getChildAnimations(options.delay)]);
  }
}
/**
 * @internal
 */
function animateTarget(visualElement, definition) {
  var _ref4 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    _ref4$delay = _ref4.delay,
    delay = _ref4$delay === void 0 ? 0 : _ref4$delay,
    transitionOverride = _ref4.transitionOverride,
    type = _ref4.type;
  var _a;
  var _visualElement$makeTa = visualElement.makeTargetAnimatable(definition),
    _visualElement$makeTa2 = _visualElement$makeTa.transition,
    transition = _visualElement$makeTa2 === void 0 ? visualElement.getDefaultTransition() : _visualElement$makeTa2,
    transitionEnd = _visualElement$makeTa.transitionEnd,
    target = _objectWithoutProperties(_visualElement$makeTa, _excluded);
  var willChange = visualElement.getValue("willChange");
  if (transitionOverride) transition = transitionOverride;
  var animations = [];
  var animationTypeState = type && ((_a = visualElement.animationState) === null || _a === void 0 ? void 0 : _a.getState()[type]);
  var _loop = function _loop(key) {
    var value = visualElement.getValue(key);
    var valueTarget = target[key];
    if (!value || valueTarget === undefined || animationTypeState && shouldBlockAnimation(animationTypeState, key)) {
      return "continue";
    }
    var valueTransition = _objectSpread({
      delay: delay
    }, transition);
    /**
     * Make animation instant if this is a transform prop and we should reduce motion.
     */
    if (visualElement.shouldReduceMotion && transformProps.has(key)) {
      valueTransition = _objectSpread(_objectSpread({}, valueTransition), {}, {
        type: false,
        delay: 0
      });
    }
    var animation = startAnimation(key, value, valueTarget, valueTransition);
    if (isWillChangeMotionValue(willChange)) {
      willChange.add(key);
      animation = animation.then(function () {
        return willChange.remove(key);
      });
    }
    animations.push(animation);
  };
  for (var key in target) {
    var _ret = _loop(key);
    if (_ret === "continue") continue;
  }
  return Promise.all(animations).then(function () {
    transitionEnd && setTarget(visualElement, transitionEnd);
  });
}
function animateChildren(visualElement, variant) {
  var delayChildren = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var staggerChildren = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var staggerDirection = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 1;
  var options = arguments.length > 5 ? arguments[5] : undefined;
  var animations = [];
  var maxStaggerDuration = (visualElement.variantChildren.size - 1) * staggerChildren;
  var generateStaggerDuration = staggerDirection === 1 ? function () {
    var i = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    return i * staggerChildren;
  } : function () {
    var i = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    return maxStaggerDuration - i * staggerChildren;
  };
  Array.from(visualElement.variantChildren).sort(sortByTreeOrder).forEach(function (child, i) {
    animations.push(animateVariant(child, variant, _objectSpread(_objectSpread({}, options), {}, {
      delay: delayChildren + generateStaggerDuration(i)
    })).then(function () {
      return child.notify("AnimationComplete", variant);
    }));
  });
  return Promise.all(animations);
}
function stopAnimation(visualElement) {
  visualElement.values.forEach(function (value) {
    return value.stop();
  });
}
function sortByTreeOrder(a, b) {
  return a.sortNodePosition(b);
}
/**
 * Decide whether we should block this animation. Previously, we achieved this
 * just by checking whether the key was listed in protectedKeys, but this
 * posed problems if an animation was triggered by afterChildren and protectedKeys
 * had been set to true in the meantime.
 */
function shouldBlockAnimation(_ref5, key) {
  var protectedKeys = _ref5.protectedKeys,
    needsAnimating = _ref5.needsAnimating;
  var shouldBlock = protectedKeys.hasOwnProperty(key) && needsAnimating[key] !== true;
  needsAnimating[key] = false;
  return shouldBlock;
}
export { animateVisualElement, sortByTreeOrder, stopAnimation };